import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useTenant } from '@/contexts/TenantContext';
import axios from 'axios';
import {
  Lock, Unlock, DoorOpen, Car, Zap, Wind, Loader2,
  Home, Key, Info, Activity, Wifi, Phone, MapPin,
  ChevronRight, Clock, CheckCircle2, XCircle, CloudSun,
  Sun, Cloud, CloudRain, CloudSnow, Waves, Smartphone, Share2, X, Moon,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/guest-app';
const I18N_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api/i18n/public';
const UPLOADS_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api/uploads/';

// Build tenant-aware headers for guest API calls
function guestHeaders(tenantSlug) {
  return tenantSlug ? { headers: { 'X-Tenant-Slug': tenantSlug } } : {};
}

// Device type icon mapping
function DeviceIcon({ type, size = 24 }) {
  const map = {
    lock: Lock, front_door: DoorOpen, garage: Car,
    gate: DoorOpen, light: Zap, climate: Wind,
    shutter: Wind, switch: Zap,
  };
  const Icon = map[type] || Key;
  return <Icon size={size} strokeWidth={1.5} />;
}

// Format seconds to Xd Xh Xm Xs
function formatCountdown(secs) {
  if (secs <= 0) return { d: 0, h: 0, m: 0, s: 0 };
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  return { d, h, m, s };
}

// Detect device type for PWA guide
function getPlatform() {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

const TAB_IDS = [
  { id: 'home', icon: Home },
  { id: 'access', icon: Key },
  { id: 'info', icon: Info },
  { id: 'activity', icon: Activity },
];

const PWA_DISMISSED_KEY = 'slux_pwa_dismissed';

export default function GuestApp() {
  const { tokenId } = useParams();
  const [searchParams] = useSearchParams();
  const tenantSlug = searchParams.get('tenant') || localStorage.getItem('tenant_slug') || '';
  const { theme, toggleTheme } = useTheme();
  const { whitelabel: wl } = useTenant();
  const [booking, setBooking] = useState(null);
  const [status, setStatus] = useState('loading');
  const [remaining, setRemaining] = useState(0);
  const [tab, setTab] = useState('home');
  const [deviceLoading, setDeviceLoading] = useState({});
  const [deviceStates, setDeviceStates] = useState({});
  const [log, setLog] = useState([]);
  const [weather, setWeather] = useState(null);
  const [lang, setLang] = useState('it');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [strings, setStrings] = useState({});
  const [showPWAGuide, setShowPWAGuide] = useState(false);
  const [isPWAInstalled] = useState(
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );

  // i18n helper
  const t = useCallback((key) =>
    strings[`${key}_${lang}`] || strings[`${key}_it`] || key,
    [strings, lang]
  );

  // Fetch i18n strings
  useEffect(() => {
    fetch(I18N_URL)
      .then(r => r.json())
      .then(d => setStrings(d))
      .catch(() => {});
  }, []);

  // Show PWA guide after first load (not if already installed or dismissed)
  useEffect(() => {
    if (!isPWAInstalled && !localStorage.getItem(PWA_DISMISSED_KEY)) {
      const timer = setTimeout(() => setShowPWAGuide(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [isPWAInstalled]);

  // Online / offline detection + queue flush
  const QUEUE_KEY = `slux_cmd_queue_${tokenId}`;

  const flushOfflineQueue = useCallback(async () => {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return;
    const queue = JSON.parse(raw);
    if (!queue.length) return;
    localStorage.removeItem(QUEUE_KEY);
    for (const cmd of queue) {
      try {
      await axios.post(`${API}/device/${cmd.deviceId}/command`, {
        action: cmd.action,
        token_id: cmd.tokenId,
        booking_id: cmd.bookingId || '',
      }, guestHeaders(tenantSlug));
        setDeviceStates(p => ({ ...p, [cmd.deviceId]: cmd.action === 'open' ? 'open' : 'closed' }));
      } catch { /* best effort */ }
    }
  }, [QUEUE_KEY, tokenId, tenantSlug]);

  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); flushOfflineQueue(); };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [flushOfflineQueue]);

  // Load booking data
  const loadBooking = useCallback(async () => {
    if (!tokenId) { setStatus('invalid'); return; }
    try {
      const res = await axios.get(`${API}/validate/${tokenId}`, guestHeaders(tenantSlug));
      const data = res.data;
      setBooking(data);
      if (data.devices) {
        const states = {};
        data.devices.forEach(d => { states[d.id] = d.state || 'unknown'; });
        setDeviceStates(states);
      }
      if (data.valid) {
        const remainSecs = data.remaining_seconds ?? 0;
        if (remainSecs > 0) {
          setStatus('active');
          setRemaining(Math.floor(remainSecs));
        } else {
          setStatus('expired');
        }
      } else {
        const reason = data.reason || '';
        if (reason.includes('scaduta') || reason.includes('expired')) setStatus('expired');
        else setStatus('invalid');
      }
    } catch (err) {
      const code = err.response?.status;
      if (code === 404 || code === 403) setStatus('invalid');
      else if (code === 410) setStatus('expired');
      else setStatus('invalid');
    }
  }, [tokenId, tenantSlug]);

  useEffect(() => { loadBooking(); }, [loadBooking]);

  // Countdown timer
  useEffect(() => {
    if (status !== 'active') return;
    const iv = setInterval(() => {
      setRemaining(r => {
        if (r <= 1) { setStatus('expired'); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [status]);

  // Load activity log when tab is active
  const loadLog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/activity-log/${tokenId}`, guestHeaders(tenantSlug));
      setLog(res.data?.items || []);
    } catch { /* ignore */ }
  }, [tokenId, tenantSlug]);

  useEffect(() => {
    if (tab === 'activity') loadLog();
  }, [tab, loadLog]);

  // Device command
  const sendCommand = async (device) => {
    const cur = deviceStates[device.id];
    const isOpen = cur === 'closed' || cur === 'locked' || cur === 'unknown';
    const action = isOpen ? 'open' : 'close';
    setDeviceLoading(p => ({ ...p, [device.id]: true }));

    if (!isOnline) {
      const raw = localStorage.getItem(QUEUE_KEY);
      const queue = raw ? JSON.parse(raw) : [];
      queue.push({ tokenId, deviceId: device.id, bookingId: booking?.booking_id || '', action, queuedAt: Date.now() });
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
      setDeviceStates(p => ({ ...p, [device.id]: action === 'open' ? 'open' : 'closed' }));
      setDeviceLoading(p => ({ ...p, [device.id]: false }));
      return;
    }

    try {
      await axios.post(`${API}/device/${device.id}/command`, {
        action,
        token_id: tokenId,
        booking_id: booking?.booking_id || '',
      }, guestHeaders(tenantSlug));
      setDeviceStates(p => ({ ...p, [device.id]: isOpen ? 'open' : 'closed' }));
      if (tab === 'activity') loadLog();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore invio comando');
    } finally {
      setDeviceLoading(p => ({ ...p, [device.id]: false }));
    }
  };

  const dismissPWA = () => {
    localStorage.setItem(PWA_DISMISSED_KEY, '1');
    setShowPWAGuide(false);
  };

  // ---- Derived data ----
  const propInfo = booking?.property_info || {};
  const devices = booking?.devices || [];
  const guestFirstName = booking?.guest_name?.split(' ')[0] || '';
  const cd = formatCountdown(remaining);

  // Weather info (bilingual)
  const weatherLabels = lang === 'it'
    ? { 0: 'Sereno', 3: 'Parz. nuvoloso', 48: 'Nebbia', 67: 'Pioggia', 77: 'Neve', 82: 'Rovesci', 99: 'Temporale' }
    : { 0: 'Clear', 3: 'Partly cloudy', 48: 'Foggy', 67: 'Rain', 77: 'Snow', 82: 'Showers', 99: 'Storm' };
  const getWeatherLabel = (code) => {
    if (code === 0) return weatherLabels[0];
    if (code <= 3) return weatherLabels[3];
    if (code <= 48) return weatherLabels[48];
    if (code <= 67) return weatherLabels[67];
    if (code <= 77) return weatherLabels[77];
    if (code <= 82) return weatherLabels[82];
    return weatherLabels[99];
  };
  const getWeatherIcon = (code) => {
    if (code === 0) return { icon: Sun, color: '#fbbf24' };
    if (code <= 3) return { icon: CloudSun, color: '#93c5fd' };
    if (code <= 48) return { icon: Cloud, color: '#9ca3af' };
    if (code <= 67) return { icon: CloudRain, color: '#60a5fa' };
    if (code <= 77) return { icon: CloudSnow, color: '#e2e8f0' };
    if (code <= 82) return { icon: CloudRain, color: '#818cf8' };
    return { icon: Waves, color: '#f87171' };
  };

  const platform = getPlatform();

  // ---- Loading ----
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[var(--lux-bg)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border border-[#c9a84c]/30 border-t-[#c9a84c] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--lux-text-muted-3)] text-sm">{lang === 'it' ? 'Caricamento...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  // ---- Expired ----
  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-[var(--lux-bg)] flex flex-col items-center justify-center px-8 text-center">
        <div className="w-16 h-16 rounded-full bg-[var(--lux-surface-3)] flex items-center justify-center mb-6">
          <Lock size={28} className="text-[var(--lux-text-muted-3)]" strokeWidth={1} />
        </div>
        <p className="text-[#c9a84c] text-[10px] uppercase tracking-[0.3em] mb-3">
          {t('expired_title')}
        </p>
        <p className="text-[var(--lux-text-muted)] text-sm leading-relaxed max-w-xs">{t('expired_subtitle')}</p>
        <button onClick={() => setLang(l => l === 'it' ? 'en' : 'it')}
          className="mt-8 text-[var(--lux-border-4)] text-xs uppercase tracking-widest">
          {lang === 'it' ? 'EN' : 'IT'}
        </button>
      </div>
    );
  }

  // ---- Invalid ----
  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-[var(--lux-bg)] flex flex-col items-center justify-center px-8 text-center">
        <div className="w-16 h-16 rounded-full bg-[var(--lux-surface-3)] flex items-center justify-center mb-6">
          <Key size={28} className="text-[var(--lux-text-muted-3)]" strokeWidth={1} />
        </div>
        <p className="text-[#c9a84c] text-[10px] uppercase tracking-[0.3em] mb-3">
          {t('invalid_title')}
        </p>
        <p className="text-[var(--lux-text-muted)] text-sm leading-relaxed max-w-xs">{t('invalid_subtitle')}</p>
        <button onClick={() => setLang(l => l === 'it' ? 'en' : 'it')}
          className="mt-8 text-[var(--lux-border-4)] text-xs uppercase tracking-widest">
          {lang === 'it' ? 'EN' : 'IT'}
        </button>
      </div>
    );
  }

  // ---- Active ----
  return (
    <div className="min-h-screen bg-[var(--lux-bg)] flex flex-col max-w-md mx-auto relative overflow-hidden">

      {/* Offline banner */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 max-w-md mx-auto z-50 bg-orange-900/90 backdrop-blur-sm text-orange-200 text-xs text-center py-2 px-4">
          {lang === 'it'
            ? 'Modalità offline — i comandi vengono accodati e inviati al ripristino della connessione'
            : 'Offline mode — commands are queued and sent when connection is restored'}
        </div>
      )}

      {/* PWA Install Guide Modal */}
      {showPWAGuide && !isPWAInstalled && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={dismissPWA} />
          <div className="relative w-full max-w-md bg-[var(--lux-surface-2)] border-t border-[var(--lux-border-2)] rounded-t-3xl px-6 pt-6 pb-10 z-10">
            <button onClick={dismissPWA} className="absolute top-4 right-4 text-[var(--lux-text-muted-3)] hover:text-white">
              <X size={20} />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#c9a84c]/10 rounded-xl flex items-center justify-center">
                <Smartphone size={20} className="text-[#c9a84c]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-white font-medium">{t('pwa_guide_title')}</p>
                <p className="text-[var(--lux-text-muted-2)] text-xs">{t('pwa_guide_subtitle')}</p>
              </div>
            </div>

            {/* iOS instructions */}
            {(platform === 'ios' || platform === 'other') && (
              <div className="mb-4">
                <p className="text-[#c9a84c] text-[10px] uppercase tracking-widest mb-2">iPhone / iPad (Safari)</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <span className="w-5 h-5 bg-[#c9a84c]/20 text-[#c9a84c] rounded-full text-xs flex items-center justify-center shrink-0 mt-0.5">1</span>
                    <div className="flex items-center gap-2">
                      <Share2 size={14} className="text-[var(--lux-text-muted)] shrink-0" />
                      <p className="text-[#aaa] text-sm">{t('pwa_ios_step1')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-5 h-5 bg-[#c9a84c]/20 text-[#c9a84c] rounded-full text-xs flex items-center justify-center shrink-0 mt-0.5">2</span>
                    <p className="text-[#aaa] text-sm">{t('pwa_ios_step2')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Android instructions */}
            {(platform === 'android' || platform === 'other') && (
              <div className="mb-5">
                <p className="text-[#c9a84c] text-[10px] uppercase tracking-widest mb-2">Android (Chrome)</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <span className="w-5 h-5 bg-[#c9a84c]/20 text-[#c9a84c] rounded-full text-xs flex items-center justify-center shrink-0 mt-0.5">1</span>
                    <p className="text-[#aaa] text-sm">{t('pwa_android_step1')}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="w-5 h-5 bg-[#c9a84c]/20 text-[#c9a84c] rounded-full text-xs flex items-center justify-center shrink-0 mt-0.5">2</span>
                    <p className="text-[#aaa] text-sm">{t('pwa_android_step2')}</p>
                  </div>
                </div>
              </div>
            )}

            <button onClick={dismissPWA}
              className="w-full bg-[#c9a84c] text-black rounded-2xl py-3 font-medium text-sm">
              {t('pwa_dismiss')}
            </button>
          </div>
        </div>
      )}

      {/* Content area */}
      <div className={`flex-1 overflow-y-auto pb-20 ${!isOnline ? 'pt-8' : ''}`}>

        {/* ===== HOME TAB ===== */}
        {tab === 'home' && (
          <div>
            {/* Hero image with gradient */}
            <div className="relative h-56 bg-gradient-to-br from-[#1a1209] to-[var(--lux-bg)] overflow-hidden">
              {propInfo.logo_url ? (
                <img src={`${UPLOADS_BASE}${propInfo.logo_url}`} alt="logo"
                  className="w-full h-full object-cover opacity-40" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#c9a84c]/5 to-transparent" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--lux-bg)] via-[var(--lux-bg)]/60 to-transparent" />
              {/* Header: logo + lang switch */}
              <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 pt-safe pt-6">
                {wl?.enabled && wl?.logo_url ? (
                  <img
                    src={wl.logo_url.startsWith('http') ? wl.logo_url : `${process.env.REACT_APP_BACKEND_URL || ''}${wl.logo_url}`}
                    alt={wl.brand_name || 'logo'}
                    className="h-6 object-contain max-w-[120px]"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <p className="text-[10px] uppercase tracking-[0.3em]"
                    style={{ color: wl?.enabled && wl?.primary_color ? wl.primary_color : '#c9a84c' }}>
                    {wl?.enabled && wl?.brand_name ? wl.brand_name : 'Shangrilux'}
                  </p>
                )}
                <div className="flex items-center gap-3">
                  {/* PWA install button (if not installed) */}
                  {!isPWAInstalled && (
                    <button onClick={() => setShowPWAGuide(true)}
                      className="text-white/40 hover:text-white/70 transition-colors" title={t('install_app')}>
                      <Smartphone size={14} />
                    </button>
                  )}
                  <button onClick={toggleTheme}
                    className="text-white/40 hover:text-white/70 transition-colors" title={theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}>
                    {theme === 'dark' ? <Sun size={13} /> : <Moon size={13} />}
                  </button>
                  <button onClick={() => setLang(l => l === 'it' ? 'en' : 'it')}
                    className="text-white/50 text-xs uppercase tracking-widest bg-black/30 px-2 py-1 rounded">
                    {lang === 'it' ? 'EN' : 'IT'}
                  </button>
                </div>
              </div>
            </div>

            {/* Welcome */}
            <div className="px-5 -mt-8 relative z-10 space-y-4">
              <div>
                <p className="text-[#c9a84c] text-[10px] uppercase tracking-[0.2em]">{t('welcome_label')}</p>
                <h1 className="font-serif text-3xl text-white font-light mt-0.5">{guestFirstName}</h1>
                <p className="text-[var(--lux-text-muted)] text-sm mt-1">{booking?.property_name}</p>
              </div>

              {/* Welcome message (dynamic from property) */}
              {(propInfo[`welcome_message_${lang}`] || propInfo.welcome_message_it) && (
                <div className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-2xl p-4">
                  <p className="text-[var(--lux-text-sec)] text-sm leading-relaxed">
                    {propInfo[`welcome_message_${lang}`] || propInfo.welcome_message_it}
                  </p>
                </div>
              )}

              {/* Stay info card */}
              <div className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-2xl p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[var(--lux-text-muted-3)] text-[10px] uppercase tracking-widest">{t('checkin_label')}</p>
                    <p className="text-white text-sm font-medium mt-0.5">
                      {new Date(booking.checkin_at).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-GB', { day: 'numeric', month: 'short' })}
                    </p>
                    <p className="text-[#c9a84c] text-xs">{propInfo.checkin_time || '15:00'}</p>
                  </div>
                  <div>
                    <p className="text-[var(--lux-text-muted-3)] text-[10px] uppercase tracking-widest">{t('checkout_label')}</p>
                    <p className="text-white text-sm font-medium mt-0.5">
                      {new Date(booking.checkout_at).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-GB', { day: 'numeric', month: 'short' })}
                    </p>
                    <p className="text-[#c9a84c] text-xs">{propInfo.checkout_time || '11:00'}</p>
                  </div>
                </div>
                {/* Countdown */}
                <div className="border-t border-[var(--lux-border-1)] pt-3">
                  <p className="text-[var(--lux-text-muted-3)] text-[10px] uppercase tracking-widest mb-2">{t('remaining_time')}</p>
                  <div className="flex gap-3">
                    {[
                      { val: cd.d, key: 'days' },
                      { val: cd.h, key: 'hours' },
                      { val: cd.m, key: 'minutes' },
                      { val: cd.s, key: 'seconds' },
                    ].map(({ val, key }) => (
                      <div key={key} className="text-center">
                        <p className="font-serif text-2xl text-white font-light">{String(val).padStart(2, '0')}</p>
                        <p className="text-[var(--lux-border-4)] text-[9px] uppercase">{t(key)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Weather widget */}
              {weather && (() => {
                const { icon: WIcon, color } = getWeatherIcon(weather.code);
                return (
                  <div className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-2xl p-4 flex items-center gap-4">
                    <WIcon size={36} strokeWidth={1} style={{ color }} />
                    <div>
                      <p className="text-white font-medium">{weather.temp}°C — {getWeatherLabel(weather.code)}</p>
                      <p className="text-[var(--lux-text-muted-2)] text-xs">{weather.city} · {t('wind_label')} {weather.wind} km/h</p>
                    </div>
                  </div>
                );
              })()}

              {/* Quick access CTA */}
              {devices.length > 0 && (
                <button onClick={() => setTab('access')}
                  className="w-full bg-[#c9a84c] hover:bg-[#b8973e] rounded-2xl p-4 flex items-center justify-between transition-colors">
                  <div className="flex items-center gap-3">
                    <Key size={18} className="text-black" strokeWidth={2} />
                    <span className="text-black font-medium text-sm">{t('manage_access')}</span>
                  </div>
                  <ChevronRight size={18} className="text-black/60" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* ===== ACCESS TAB ===== */}
        {tab === 'access' && (
          <div className="px-5 pt-8 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#c9a84c] text-[10px] uppercase tracking-[0.2em]">{t('devices_title')}</p>
                <h2 className="font-serif text-2xl text-white font-light mt-0.5">{t('access_section_title')}</h2>
              </div>
              <button onClick={() => setLang(l => l === 'it' ? 'en' : 'it')}
                className="text-white/40 text-xs uppercase tracking-widest bg-black/30 px-2 py-1 rounded">
                {lang === 'it' ? 'EN' : 'IT'}
              </button>
            </div>

            {devices.length === 0 ? (
              <div className="text-center py-16">
                <Key size={36} className="text-[var(--lux-border-3)] mx-auto mb-4" strokeWidth={1} />
                <p className="text-[var(--lux-text-muted-3)] text-sm">{t('no_devices')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {devices.map(device => {
                  const curState = deviceStates[device.id];
                  const isOpen = curState === 'open';
                  const isLoading = deviceLoading[device.id];
                  const stateLabel = isOpen ? t('device_open')
                    : curState === 'closed' || curState === 'locked' ? t('device_closed')
                    : t('device_ready');
                  return (
                    <div key={device.id}
                      className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-2xl p-5 flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 transition-all ${
                        isOpen ? 'bg-[#c9a84c]/20' : 'bg-[var(--lux-surface-3)]'
                      }`}>
                        <span className={isOpen ? 'text-[#c9a84c]' : 'text-[var(--lux-text-muted-3)]'}>
                          <DeviceIcon type={device.type} size={24} />
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{device.name}</p>
                        <p className="text-[var(--lux-text-muted-3)] text-xs mt-0.5">{stateLabel}</p>
                      </div>
                      <button
                        onClick={() => sendCommand(device)}
                        disabled={isLoading}
                        data-testid={`device-btn-${device.id}`}
                        className={`shrink-0 w-14 h-14 rounded-full flex items-center justify-center transition-all active:scale-95 ${
                          isOpen
                            ? 'bg-red-900/40 border border-red-800/60 text-red-400 hover:bg-red-900/60'
                            : 'bg-[#c9a84c] text-black hover:bg-[#b8973e]'
                        }`}
                      >
                        {isLoading ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : isOpen ? (
                          <Lock size={18} strokeWidth={2} />
                        ) : (
                          <Unlock size={18} strokeWidth={2} />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ===== INFO TAB ===== */}
        {tab === 'info' && (
          <div className="px-5 pt-8 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#c9a84c] text-[10px] uppercase tracking-[0.2em]">{t('property_label')}</p>
                <h2 className="font-serif text-2xl text-white font-light mt-0.5">{t('info_section_title')}</h2>
              </div>
              <button onClick={() => setLang(l => l === 'it' ? 'en' : 'it')}
                className="text-white/40 text-xs uppercase tracking-widest bg-black/30 px-2 py-1 rounded">
                {lang === 'it' ? 'EN' : 'IT'}
              </button>
            </div>

            {/* WiFi */}
            {propInfo.wifi_name && (
              <div className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Wifi size={14} className="text-[#c9a84c]" />
                  <p className="text-[#c9a84c] text-[10px] uppercase tracking-widest">{t('wifi_section_title')}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[var(--lux-text-muted-3)] text-[10px] uppercase mb-1">{t('wifi_network_label')}</p>
                    <p className="text-white font-medium">{propInfo.wifi_name}</p>
                  </div>
                  <div>
                    <p className="text-[var(--lux-text-muted-3)] text-[10px] uppercase mb-1">{t('wifi_password_label')}</p>
                    <p className="text-white font-medium font-mono">{propInfo.wifi_password || '—'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Check-in / Check-out times */}
            <div className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={14} className="text-[#c9a84c]" />
                <p className="text-[#c9a84c] text-[10px] uppercase tracking-widest">{t('times_title')}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[var(--lux-text-muted-3)] text-[10px] uppercase mb-1">{t('checkin_label')}</p>
                  <p className="text-white font-medium">{propInfo.checkin_time || '15:00'}</p>
                </div>
                <div>
                  <p className="text-[var(--lux-text-muted-3)] text-[10px] uppercase mb-1">{t('checkout_label')}</p>
                  <p className="text-white font-medium">{propInfo.checkout_time || '11:00'}</p>
                </div>
              </div>
            </div>

            {/* House rules */}
            {propInfo.house_rules && (
              <div className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-2xl p-5">
                <p className="text-[#c9a84c] text-[10px] uppercase tracking-widest mb-3">{t('house_rules_title')}</p>
                <p className="text-[var(--lux-text-sec)] text-sm leading-relaxed whitespace-pre-line">{propInfo.house_rules}</p>
              </div>
            )}

            {/* Emergency contacts */}
            {propInfo.emergency_contacts?.length > 0 && (
              <div className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Phone size={14} className="text-[#c9a84c]" />
                  <p className="text-[#c9a84c] text-[10px] uppercase tracking-widest">{t('contacts_title')}</p>
                </div>
                <div className="space-y-3">
                  {propInfo.emergency_contacts.map((c, i) => (
                    <a key={i} href={`tel:${c.phone}`} className="flex items-center justify-between py-1 group">
                      <p className="text-white text-sm">{c.name}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-[#c9a84c] font-mono text-sm">{c.phone}</span>
                        <Phone size={13} className="text-[var(--lux-text-muted-3)] group-hover:text-[#c9a84c] transition-colors" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Local guide */}
            {propInfo.local_guide?.length > 0 && (
              <div className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin size={14} className="text-[#c9a84c]" />
                  <p className="text-[#c9a84c] text-[10px] uppercase tracking-widest">{t('local_guide_title')}</p>
                </div>
                {(() => {
                  const groups = {};
                  propInfo.local_guide.forEach(g => {
                    const cat = g.category || 'Altro';
                    if (!groups[cat]) groups[cat] = [];
                    groups[cat].push(g);
                  });
                  return Object.entries(groups).map(([cat, places]) => (
                    <div key={cat} className="mb-4 last:mb-0">
                      <p className="text-[var(--lux-text-muted-3)] text-[10px] uppercase tracking-widest mb-2">{cat}</p>
                      <div className="space-y-2.5">
                        {places.map((g, i) => (
                          <div key={i} className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-white text-sm">{g.name}</p>
                              {g.address && <p className="text-[var(--lux-text-muted-3)] text-xs">{g.address}</p>}
                            </div>
                            {g.phone && (
                              <a href={`tel:${g.phone}`} className="text-[#c9a84c] text-xs shrink-0">{g.phone}</a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}

            {!propInfo.wifi_name && !propInfo.house_rules && !propInfo.emergency_contacts?.length && !propInfo.local_guide?.length && (
              <div className="text-center py-16">
                <Info size={36} className="text-[var(--lux-border-3)] mx-auto mb-4" strokeWidth={1} />
                <p className="text-[var(--lux-text-muted-3)] text-sm">{t('no_info')}</p>
              </div>
            )}
          </div>
        )}

        {/* ===== ACTIVITY TAB ===== */}
        {tab === 'activity' && (
          <div className="px-5 pt-8 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#c9a84c] text-[10px] uppercase tracking-[0.2em]">Log</p>
                <h2 className="font-serif text-2xl text-white font-light mt-0.5">{t('activity_section_title')}</h2>
              </div>
              <button onClick={() => setLang(l => l === 'it' ? 'en' : 'it')}
                className="text-white/40 text-xs uppercase tracking-widest bg-black/30 px-2 py-1 rounded">
                {lang === 'it' ? 'EN' : 'IT'}
              </button>
            </div>
            {log.length === 0 ? (
              <div className="text-center py-16">
                <Activity size={36} className="text-[var(--lux-border-3)] mx-auto mb-4" strokeWidth={1} />
                <p className="text-[var(--lux-text-muted-3)] text-sm">{t('no_activity')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {log.map((entry, i) => {
                  const isSuccess = entry.success !== false;
                  return (
                    <div key={i} className="bg-[var(--lux-surface-1)] border border-[var(--lux-border-1)] rounded-xl px-4 py-3 flex items-start gap-3">
                      <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                        isSuccess ? 'bg-green-900/40' : 'bg-red-900/40'}`}>
                        {isSuccess
                          ? <CheckCircle2 size={11} className="text-green-400" />
                          : <XCircle size={11} className="text-red-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm">{entry.device_name || t('devices_title')}</p>
                        <p className="text-[var(--lux-text-muted-3)] text-xs">
                          {entry.action} · {new Date(entry.timestamp).toLocaleString(lang === 'it' ? 'it-IT' : 'en-GB')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== BOTTOM NAV ===== */}
      <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-[var(--lux-bg)]/95 backdrop-blur-xl border-t border-[var(--lux-border-1)] flex z-40">
        {TAB_IDS.map(({ id, icon: Icon }) => {
          const active = tab === id;
          return (
            <button key={id} onClick={() => setTab(id)}
              data-testid={`tab-${id}`}
              className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 transition-all ${
                active ? 'text-[#c9a84c]' : 'text-[var(--lux-border-4)] hover:text-[var(--lux-text-muted)]'
              }`}>
              <Icon size={20} strokeWidth={active ? 2 : 1.5} />
              <span className="text-[8px] sm:text-[9px] uppercase tracking-wide">{t(`tab_${id}`)}</span>
              {active && <div className="w-1 h-1 rounded-full bg-[#c9a84c]" />}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
